import { useState } from "react";
import { Target, Check, Clock, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { useDailyChallenges, DailyChallenge } from "@/hooks/useDailyChallenges";
import { useLanguage } from "@/i18n/LanguageContext";

const ChallengeItem = ({ challenge }: { challenge: DailyChallenge }) => {
  const progress = Math.min((challenge.current / challenge.target) * 100, 100);

  return (
    <div
      className={`p-4 border transition-all duration-300 ${
        challenge.completed
          ? "border-emerald-500/30 bg-emerald-500/5"
          : "border-border/20 bg-card/30 hover:border-border/40"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {challenge.completed ? (
              <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            ) : (
              <Target className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
            )}
            <h4
              className={`text-sm font-medium truncate ${
                challenge.completed ? "text-emerald-400" : "text-foreground/80"
              }`}
            >
              {challenge.title}
            </h4>
          </div>
          <p className="text-xs text-muted-foreground/60 mb-2 pl-6">{challenge.description}</p>

          {/* Progress bar */}
          <div className="pl-6">
            <div className="h-1.5 bg-foreground/5 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  challenge.completed ? "bg-emerald-500" : "bg-foreground/20"
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-muted-foreground/40">
                {challenge.type === "time"
                  ? `${Math.floor(challenge.current / 60)}/${Math.floor(challenge.target / 60)} min`
                  : `${challenge.current}/${challenge.target}`}
              </span>
              {challenge.completed && (
                <span className="text-[10px] text-emerald-500/70 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {challenge.reward}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DailyChallengesCard = () => {
  const [expanded, setExpanded] = useState(true);
  const { challenges, totalCompleted, getCompletedCount } = useDailyChallenges();
  const { t } = useLanguage();

  const completedToday = getCompletedCount();
  const allCompleted = completedToday === challenges.length;

  return (
    <div
      className={`border transition-all duration-300 ${
        allCompleted
          ? "border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-transparent"
          : "border-border/20 bg-card/20"
      }`}
    >
      {/* Header */}
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-center justify-between group">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${allCompleted ? "bg-emerald-500/10" : "bg-foreground/5"}`}>
            <Target className={`w-5 h-5 ${allCompleted ? "text-emerald-500" : "text-amber-500"}`} />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-medium text-foreground/90">{t("daily_challenges") || "Daily Challenges"}</h3>
            <p className="text-xs text-muted-foreground/50">
              {completedToday}/{challenges.length} {t("challenges_completed") || "completed"} • {totalCompleted}{" "}
              {t("challenges_total") || "total"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Completion indicator */}
          <div className="flex gap-1">
            {challenges.map((c, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  c.completed ? "bg-emerald-500" : "bg-foreground/10"
                }`}
              />
            ))}
          </div>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
          )}
        </div>
      </button>

      {/* Challenges list */}
      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          {challenges.map((challenge) => (
            <ChallengeItem key={challenge.id} challenge={challenge} />
          ))}

          {/* Reset timer */}
          <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground/30">
            <Clock className="w-3 h-3" />
            <span>{t("resets_midnight") || "Resets at midnight"}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyChallengesCard;
